import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { VideoPlayer, type VideoPlayerRef } from "@msuite/ui/video-player";
import { useCallback, useEffect, useRef, useState } from "react";
import { client } from "@/hono";
import { carryFrom, clampToKept, type KeptRange, mergeTouching, splitAt } from "./cut-segments";
import { CutVideoTimeline } from "./cut-video-timeline";

/** How far off a boundary the player may land before it is pushed back onto it. */
const SEEK_TOLERANCE = 0.1;

/** Props Interface */
interface CutVideoModalProps extends ModalProps {
	videoId: string;
}

/**
 * The cut editor: the whole recording, with what the cut removes drawn on it.
 *
 * It edits the **source** rather than the published video, which is what makes a cut adjustable —
 * a stretch trimmed too tightly last week can be dragged open again this week, because the
 * material was never thrown away. That is also why the source is behind its own route and its own
 * rule: what a previous cut removed is shown to somebody entitled to change that cut, and to
 * nobody else.
 */
export function CutVideoModal({ isOpen, onClose, videoId }: CutVideoModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const player = useRef<VideoPlayerRef | null>(null);
	/** Read by the follow loop, which outlives any one render and must not close over a stale list. */
	const keptRanges = useRef<KeptRange[] | null>(null);
	const frame = useRef<number | null>(null);
	/** The boundary the player was last pushed back to, so one seek is corrected once. */
	const lastClamp = useRef<number | null>(null);
	/** Whether the playhead has been put on the cut's first frame, which happens once per opening. */
	const hasPositioned = useRef(false);
	const detachPlayer = useRef<(() => void) | null>(null);

	/** State */
	const [segments, setSegments] = useState<KeptRange[] | null>(null);
	const [currentTime, setCurrentTime] = useState(0);
	const [isAsking, setIsAsking] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	/** Query */
	const editor = useQuery({
		queryKey: ["video-editor", videoId],
		queryFn: async () => {
			const response = await client.videos[":videoId"].editor.$get({ param: { videoId } });
			const data = await responseHandler(response);
			return data.editor;
		},
		retry: false,
		// The tokens are signed for an hour and the stills for the same, so a refetch behind the
		// author's back would replace every image URL while they are dragging on top of them.
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		// And it lives exactly as long as the editor does. Kept past that, the next opening would
		// draw the state the last cut was made against: the source's playback id — which a
		// discarded original has since had retired at Mux, so the player says the video does not
		// exist — and the source's length, which the new source no longer has. Never going stale
		// and outliving the thing it describes are the same setting seen twice.
		gcTime: 0,
	});

	/** Effects */
	// The editor opens on what is already cut — a cut that failed first, because that selection is
	// the author's own work and this is where they come back to it.
	useEffect(() => {
		if (!editor.data || segments) return;
		const stored = editor.data.pendingSegments ?? editor.data.segments;
		const kept = stored?.length
			? (stored as KeptRange[])
			: [{ startTime: 0, endTime: editor.data.duration }];
		setSegments(kept);
		// The editor opens on the first frame the cut keeps, not on the source's own first frame:
		// on a video that already has a cut those are different, and the source's is one the
		// playhead is not allowed to stand on.
		setCurrentTime(kept.at(0)?.startTime ?? 0);
	}, [editor.data, segments]);

	useEffect(() => {
		keptRanges.current = segments;
		const first = segments?.at(0);
		if (!first || hasPositioned.current || !player.current) return;
		hasPositioned.current = true;
		player.current.currentTime = first.startTime;
	}, [segments]);

	// The space bar plays and pauses, which is what a decade of editing software has taught
	// everyone who will use this. It is taken before a focused control can act on it: activating a
	// button with the space bar is the web's convention, but losing playback to it every time the
	// scissors was clicked is the worse of the two, and Enter still presses every button here. A
	// player that has focus keeps its own shortcut rather than being toggled twice.
	useEffect(() => {
		if (!isOpen) return;

		const handleOnKeyDown = (event: KeyboardEvent) => {
			const isPlain = !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
			// `key` as well as `code`, because an event that did not come from a physical keyboard
			// often carries no `code` at all — assistive technology and automation both send them.
			const isSpace = event.code === "Space" || event.key === " ";
			if (!isSpace || event.repeat || !isPlain) return;
			const target = event.target;
			const isTyping =
				target instanceof HTMLElement &&
				target.closest("input, textarea, select, [contenteditable='true'], mux-player");
			const element = player.current;
			if (isTyping || !element) return;
			event.preventDefault();
			// A play() that loses a race with a pause rejects, and that is not a failure.
			if (element.paused) element.play()?.catch(() => {});
			else element.pause();
		};

		window.addEventListener("keydown", handleOnKeyDown);
		return () => window.removeEventListener("keydown", handleOnKeyDown);
	}, [isOpen]);

	/** Functions */
	/**
	 * Follow the player, and keep it inside the cut.
	 *
	 * Attached as a callback ref rather than from an effect, because the player is rendered only
	 * once a range exists — one render after the data it would have depended on, so an effect keyed
	 * on that data ran while the element was still null and never attached anything.
	 *
	 * The position is read on animation frames rather than from `timeupdate`, which fires about
	 * four times a second: the playhead would advance in visible steps, which is the one thing a
	 * playhead must not do.
	 *
	 * Playback is confined to what the cut keeps. Pressing play from outside the kept stretch jumps
	 * to its start, and the player stops at its end, so what the author watches here is the video
	 * the cut will produce — the timeline still shows the whole source, and clicking it still seeks
	 * anywhere, because that is how a boundary is found.
	 */
	const attachPlayer = useCallback((element: VideoPlayerRef | null) => {
		detachPlayer.current?.();
		detachPlayer.current = null;
		player.current = element;
		if (!element) return;

		const stopFollowing = () => {
			if (frame.current !== null) cancelAnimationFrame(frame.current);
			frame.current = null;
		};
		const startFollowing = () => {
			stopFollowing();
			frame.current = requestAnimationFrame(follow);
		};

		/** Set while a correction waits for its seek to land, so it can be dropped on unmount. */
		let resume: (() => void) | null = null;
		const stopResuming = () => {
			if (resume) element.removeEventListener("seeked", resume);
			resume = null;
		};

		/**
		 * Move playback to a moment and go on playing from there.
		 *
		 * The player is paused for the seek and started again once it lands. Asked to seek forward
		 * *while playing* into a stretch it has not buffered, it abandons the seek and runs to the
		 * end of the video instead — reproducible on this player with no cut involved at all, and
		 * the last part of a recording is exactly the stretch that is never buffered ahead. Every
		 * correction this editor makes is such a seek, so all of them go through here.
		 *
		 * A drag does not: its seeks are the author's own scrubbing, dozens of them a second, and
		 * pausing for each would stutter.
		 */
		const seekTo = (time: number) => {
			const wasPlaying = !element.paused;
			stopResuming();
			if (wasPlaying) element.pause();
			element.currentTime = time;
			setCurrentTime(time);
			if (!wasPlaying) return;
			resume = () => {
				stopResuming();
				// A play() that loses a race with a pause rejects, and that is not a failure.
				element.play()?.catch(() => {});
			};
			element.addEventListener("seeked", resume);
		};

		/**
		 * Carry playback over a removal, or stop it at the end of the cut.
		 *
		 * A removal is not played: reaching one, playback jumps to where the next kept stretch
		 * begins, so what the author watches here is the video the cut will produce rather than the
		 * source with markings on it. Past the last kept stretch there is nowhere to jump to, and
		 * playback stops.
		 *
		 * @returns True when playback was moved or stopped, so the caller knows to yield.
		 */
		const carryPastRemoval = () => {
			const kept = keptRanges.current;
			if (!kept) return false;
			const carry = carryFrom(kept, element.currentTime ?? 0, SEEK_TOLERANCE);
			if (carry.kind === "play") return false;

			if (carry.kind === "jump") {
				lastClamp.current = null;
				seekTo(carry.time);
				return true;
			}
			// Nothing to resume at the end of the cut, so this one pauses and stays paused.
			element.pause();
			element.currentTime = carry.time;
			setCurrentTime(carry.time);
			stopFollowing();
			return true;
		};

		/**
		 * Put the player back inside what the cut keeps, and say whether it had to.
		 *
		 * The playhead does not enter a removed stretch at all — not by clicking the timeline, not
		 * by the player's own scrubber, not by seeking mid-playback. One rule instead of three, and
		 * it is the honest one: the editor previews the video the cut produces, and the red is not
		 * part of it. To hear what is in there, move the boundary; the handle seeks as it is dragged,
		 * which is how the frame gets found anyway.
		 *
		 * A correction is applied at most once per seek. A player that cannot land exactly on the
		 * boundary would otherwise be corrected towards a position it keeps missing, for ever.
		 */
		const clampIntoCut = () => {
			const kept = keptRanges.current;
			if (!kept?.length) return false;
			const time = element.currentTime ?? 0;
			const nearest = clampToKept(kept, time);
			if (Math.abs(nearest - time) <= SEEK_TOLERANCE) return false;
			if (lastClamp.current === nearest) return false;
			lastClamp.current = nearest;
			seekTo(nearest);
			return true;
		};

		function follow() {
			if (!element) return;
			if (carryPastRemoval()) {
				// A jump ends this frame; playback continues, so the loop is picked up again by the
				// `seeked` the jump itself fires.
				return;
			}
			setCurrentTime(element.currentTime ?? 0);
			frame.current = requestAnimationFrame(follow);
		}

		const onPlay = () => {
			const kept = keptRanges.current;
			const start = kept?.at(0)?.startTime;
			const end = kept?.at(-1)?.endTime;
			// Play from the end of the cut restarts it, which is what a preview should do.
			if (
				start !== undefined &&
				end !== undefined &&
				(element.currentTime ?? 0) >= end - SEEK_TOLERANCE
			) {
				lastClamp.current = null;
				element.currentTime = start;
			}
			clampIntoCut();
			startFollowing();
		};
		const onStop = () => {
			stopFollowing();
			setCurrentTime(element.currentTime ?? 0);
		};
		const onSeeked = () => {
			if (clampIntoCut()) return;
			lastClamp.current = null;
			setCurrentTime(element.currentTime ?? 0);
			// A seek during playback must not end the loop — no `play` event follows one.
			if (!element.paused) startFollowing();
		};

		// The frame loop is what makes the playhead glide, but a browser throttles animation frames
		// in a tab nobody is looking at — and playback carries on. `timeupdate` keeps arriving there,
		// so it is what actually guarantees the stop; the frame loop is what makes it look right.
		const onTimeUpdate = () => {
			if (carryPastRemoval()) return;
			if (frame.current === null) setCurrentTime(element.currentTime ?? 0);
		};

		// A player still loading may ignore a `currentTime` written before it knows its own
		// duration, which would leave the playhead standing in the removed lead-in.
		const onLoadedMetadata = () => {
			lastClamp.current = null;
			clampIntoCut();
		};

		element.addEventListener("loadedmetadata", onLoadedMetadata);
		element.addEventListener("play", onPlay);
		element.addEventListener("pause", onStop);
		element.addEventListener("ended", onStop);
		element.addEventListener("seeked", onSeeked);
		element.addEventListener("timeupdate", onTimeUpdate);

		detachPlayer.current = () => {
			stopFollowing();
			stopResuming();
			element.removeEventListener("loadedmetadata", onLoadedMetadata);
			element.removeEventListener("play", onPlay);
			element.removeEventListener("pause", onStop);
			element.removeEventListener("ended", onStop);
			element.removeEventListener("seeked", onSeeked);
			element.removeEventListener("timeupdate", onTimeUpdate);
		};
	}, []);

	function handleOnSeek(time: number) {
		setCurrentTime(time);
		if (player.current) player.current.currentTime = time;
	}

	/**
	 * Cut where the playhead stands.
	 *
	 * It removes nothing: the kept stretch is divided in two, and the halves meet at a seam whose
	 * edges are dragged like any others. Pulling one away is what takes material out, so the author
	 * decides how much by watching the frame instead of accepting a width the editor guessed.
	 * Refused — and offered as disabled — where a half would be born too short to keep.
	 */
	function handleOnCut() {
		if (!segments) return;
		const next = splitAt(segments, currentTime);
		if (next) setSegments(next);
	}

	async function handleOnConfirm(keepSource: boolean) {
		if (!segments?.length) return;
		setIsSaving(true);
		try {
			const response = await client.videos[":videoId"].cut.$post({
				param: { videoId },
				// Seams are closed on the way out. A cut nobody pulled apart removes nothing, and
				// sending it as two ranges would have the server render a copy of the video it is
				// already playing.
				json: { segments: mergeTouching(segments), keepSource },
			});
			await responseHandler(response);
			onClose();
		} finally {
			setIsSaving(false);
		}
	}

	/** Computed */
	const data = editor.data;
	// Measured against what is **published**, not against the whole source: widening a cut back to
	// the full recording is a change like any other, and comparing against the source would refuse
	// the one edit that undoes a cut. A selection a failed render left behind differs from the
	// published one by construction, so a retry is offered without a further rule.
	const published = data?.segments?.length
		? (data.segments as KeptRange[])
		: data
			? [{ startTime: 0, endTime: data.duration }]
			: null;
	// Compared with its seams closed, for the same reason they are closed on the way out: a cut
	// made and then dragged shut again describes the published video exactly, and offering to apply
	// it would promise a change that cannot happen.
	const selection = segments ? mergeTouching(segments) : null;
	const isUnchanged =
		!!published &&
		!!selection &&
		published.length === selection.length &&
		published.every((range, index) => {
			const current = selection.at(index);
			return (
				!!current && range.startTime === current.startTime && range.endTime === current.endTime
			);
		});
	// The scissors needs a stretch long enough to divide, and a playhead standing in one.
	const canCut = !!segments && splitAt(segments, currentTime) !== null;

	/** Render */
	return (
		<Modal
			title={t("videos.cut_video")}
			isOpen={isOpen}
			onClose={onClose}
			width="52rem"
			isLoading={editor.isLoading}
			withWarning={false}
		>
			<Modal.Body>
				{data && segments ? (
					<div className="v-stack gap-4">
						{data.pendingSegments && (
							<div className="h-stack gap-2 rounded-md bg-amber-100 p-3 text-amber-900 text-sm dark:bg-amber-900/30 dark:text-amber-200">
								<Icon name="TriangleAlert" />
								<span>{t("videos.cut_failed_selection_kept")}</span>
							</div>
						)}
						<VideoPlayer
							playerRef={attachPlayer}
							playbackId={data.playbackId}
							tokens={{
								playback: data.token,
								thumbnail: data.thumbnailToken,
								storyboard: data.storyboardToken,
							}}
							aspectRatio={data.aspectRatio}
							className="mx-auto"
						/>
						<CutVideoTimeline
							duration={data.duration}
							segments={segments}
							currentTime={currentTime}
							stills={data.stills}
							isDisabled={isSaving}
							onChange={setSegments}
							onSeek={handleOnSeek}
						/>
						<div className="h-stack items-center justify-end gap-4">
							<Button
								variant="ghost"
								type="button"
								icon={<Icon name="Scissors" />}
								isDisabled={!canCut || isSaving}
								onClick={handleOnCut}
							>
								{t("videos.cut_here")}
							</Button>
						</div>
					</div>
				) : null}
			</Modal.Body>

			{/*
				Saving asks what becomes of the recording, and the two answers are the question —
				each is one click, and neither is hidden behind the other. Keeping the recording is
				what makes this cut adjustable later; discarding it is what makes the removal
				permanent, and on a screen recording of this application that is often the whole
				point. Burying the deleting answer behind a follow-up would make the safe-looking
				path the one that quietly keeps the material.

				The way out of the question is "back", not the "cancel" it replaces: cancel closes
				the editor and takes the selection with it, and somebody who only wants to think
				again about the recording should not lose the cut they just drew.
			*/}
			<Modal.Footer>
				{isAsking ? (
					<>
						<Button
							variant="ghost"
							type="button"
							isDisabled={isSaving}
							onClick={() => setIsAsking(false)}
						>
							{t("general.back")}
						</Button>
						<div className="h-stack gap-2">
							<Button
								variant="ghost"
								type="button"
								isLoading={isSaving}
								onClick={() => handleOnConfirm(false)}
							>
								{t("videos.cut_discard_original")}
							</Button>
							<Button
								type="button"
								isLoading={isSaving}
								onClick={() => handleOnConfirm(true)}
							>
								{t("videos.cut_keep_original")}
							</Button>
						</div>
					</>
				) : (
					<>
						<Button
							variant="ghost"
							type="button"
							onClick={onClose}
						>
							{t("general.cancel")}
						</Button>
						{/* No scissors here. It belongs to "cut here", the one control that divides
						    something; wearing it twice would make the two read as the same gesture. */}
						<Button
							type="button"
							isDisabled={!segments || isUnchanged}
							onClick={() => setIsAsking(true)}
						>
							{t("general.save")}
						</Button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
}
